import {
  useDisclosure,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../shared/Icons';
import { AddVdcArticleModal } from './AddVdcArticleModal';
import { formatRange } from './utils';
import { VdcConfigContext } from './VdcPricing';
import { AddedValue } from './useVdcConfiguration';
import { fadeInBottomAnimation } from '@texas/resources/animations/animations';

export function TableSection() {
  const { t } = useTranslation();
  const {
    addedValues,
    branchRegions,
    templateRangeTiers,
    templates,
    treatments,
    removeValue,
    configuredBranches,
  } = useActiveContext(VdcConfigContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedRowData, setSelectedRowData] = useState<AddedValue | null>(
    null,
  );

  const regions = useMemo(() => {
    const regionIds = new Set<number>();
    const valueRegions = branchRegions?.filter((br) =>
      br.branches.some((b) => configuredBranches.includes(b.id)),
    );
    valueRegions?.forEach((r) => regionIds.add(r.id));
    return branchRegions?.filter((r) => regionIds.has(r.id)) ?? [];
  }, [branchRegions, configuredBranches]);

  const handlePlusClick = (value: AddedValue) => {
    setSelectedRowData(value);
    onOpen();
  };

  const handleModalClose = () => {
    setSelectedRowData(null);
    onClose();
  };

  if (addedValues.length === 0) {
    return <Text variant="sub">{t('vdc.nothingAddedToTableYet')}</Text>;
  }

  return (
    <Box flex={1} overflowX="auto">
      <Table
        variant="simple"
        sx={{ borderSpacing: '0 12px', borderCollapse: 'separate' }}
      >
        <Thead border="none">
          <Tr>
            <Th minW="300px" />
            {regions.map((region) => (
              <Th key={region.id} textAlign="center">
                <Flex flexDir="column" alignItems="center">
                  <Text fontSize="lg">{region.name}</Text>
                  <Text fontSize="xs" fontWeight="medium" color="gray.300">
                    {region.branches
                      .filter((b) => configuredBranches.includes(b.id))
                      .map((b) => b.erp)
                      .join(', ')}
                  </Text>
                </Flex>
              </Th>
            ))}
            <Th w="auto" />
          </Tr>
        </Thead>
        <Tbody>
          {addedValues.map((value, index) => {
            const tier = templateRangeTiers?.find(
              (t) => t.id === value.tierRangeId,
            );
            const template = templates?.find(
              (t) => t.id === value.compositionGroupId,
            );
            const treatmentNames =
              value.treatmentIds?.map((id) => {
                return treatments?.find((t) => t.id === id)?.name;
              }) ?? [];

            return (
              <Tr
                bg="texas.bg.800"
                _light={{ bg: 'gray.50' }}
                key={index}
                borderRadius="md"
                animation={fadeInBottomAnimation()}
              >
                <Td>
                  <Flex flexDir="column" gap={2}>
                    <Flex gap={2} alignItems="center">
                      <Icons.PackageVariantClosed boxSize={5} />
                      <Text fontSize="sm" fontWeight="medium">
                        {tier
                          ? formatRange(tier.startRange, tier.endRange)
                          : 'N/A'}
                      </Text>
                    </Flex>
                    <Flex gap={2} alignItems="center">
                      <Icons.TextureBox boxSize={5} />
                      <Text fontSize="sm">
                        {template
                          ? !template.note || template.note.length == 0
                            ? t('compositionGroup.option.optionNr', {
                                nr: template.version,
                              })
                            : template.note
                          : 'N/A'}
                      </Text>
                    </Flex>
                    <Flex gap={2} alignItems="center">
                      <Icons.RulerSquare boxSize={5} />
                      <Text fontSize="sm">
                        {value.width * value.length} mm2
                      </Text>
                      <Icons.SlashForward />
                      <Text fontSize="sm">
                        {value.width}x{value.length} mm
                      </Text>
                    </Flex>
                    {value.treatmentIds &&
                      value.treatmentIds.length > 0 &&
                      treatmentNames.length > 0 && (
                        <Flex gap={2} alignItems="center">
                          <Icons.CogOutline boxSize={5} />
                          <Text fontSize="sm">{treatmentNames.join(', ')}</Text>
                        </Flex>
                      )}
                  </Flex>
                </Td>

                {regions.map((region) => {
                  const regionPrice = value.pricing?.regionPrices.find(
                    (rp) => rp.regionId === region.id,
                  );
                  return (
                    <Td key={region.id} textAlign="center">
                      <Flex
                        flexDir="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {regionPrice ? (
                          <>
                            <Text fontSize="xs" color="gray.300">
                              {t('general.sales')}
                            </Text>
                            <Text fontWeight="medium">
                              {regionPrice.salePrice.toFixed(7)} USD
                            </Text>
                          </>
                        ) : (
                          <Text fontSize="sm" color="gray.300">
                            —
                          </Text>
                        )}
                      </Flex>
                    </Td>
                  );
                })}

                <Td textAlign="center">
                  <Flex gap={2} alignItems="center">
                    <Tooltip label={t('vdc.createArticle')}>
                      <IconButton
                        icon={<Icons.Plus />}
                        aria-label="Add"
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePlusClick(value)}
                      />
                    </Tooltip>
                    <Tooltip label={t('vdc.removeFromTable')}>
                      <IconButton
                        icon={<Icons.Trash />}
                        aria-label="Delete"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeValue(index)}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {selectedRowData && (
        <AddVdcArticleModal
          isOpen={isOpen}
          onClose={handleModalClose}
          vdcPricingData={{
            productGroupId: selectedRowData.productGroupId,
            branchIds: configuredBranches,
            width: selectedRowData.width,
            length: selectedRowData.length,
            tierRangeId: selectedRowData.tierRangeId,
            compositionGroupId: selectedRowData.compositionGroupId,
            treatmentIds: selectedRowData.treatmentIds,
          }}
        />
      )}
    </Box>
  );
}
